import moment from 'moment';
import 'moment/locale/it';
import { TRANSLATIONS } from '../../translations';

import {
  ORDER_STATUSES_CLASSNAMES,
  ORDER_STATUSES_LABELS,
  ORDER_STATUSES_DESCRIPTIONS,
} from '../define/constants';

const _fmtreplace = (string, ...args) =>
  string.replace(/{(\d+)}/g, (match, i) => {
    return typeof args[i] !== 'undefined' ? args[i] : match;
  });
const _n = (singular, plural, count) => (count > 1 ? plural : singular);

export const formatCurrency = (number, trim = false, round = false) => {
  if (round) {
    number = Math.round(number);
  }

  let res = Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(number);

  if (trim) {
    res = res.replace(/,00/gi, '');
  }

  return res;
};

export const formatDate = (moment) => moment.format('LL');
export const formatDateTime = (moment) => moment.format('LLL');

export const formatDurationShort = (minutes, show_bundle_badge = false) => {
  const duration = moment.duration(minutes, 'minutes');

  if (duration.days() > 0) {
    const days = duration.hours() >= 12 ? duration.days() + 1 : duration.days();
    
    if (show_bundle_badge) {
      const nights = days - 1;
      if (nights > 0) {
        return _fmtreplace(
          _n('{0} ' + app_main_wpml.night, '{0} ' + app_main_wpml.nights, nights),
          nights
        );
      }
    }
    
    return _fmtreplace(
      _n('{0} ' + app_main_wpml.day, '{0} ' + app_main_wpml.days, days),
      days
    );
  }

  if (duration.hours() > 0) {
    let hours_count, hours_string;

    if (duration.minutes() >= 15 && duration.minutes() < 45) {
      hours_count = duration.hours() + 0.5;
      hours_string = duration.hours() + ',5';
    } else if (duration.minutes() >= 45) {
      hours_count = duration.hours() + 1;
      hours_string = duration.hours() + 1;
    } else {
      hours_count = duration.hours();
      hours_string = duration.hours();
    }

    return _fmtreplace(
      _n(
        '{0} ' + app_main_wpml.hour,
        '{0} ' + app_main_wpml.hours,
        hours_count
      ),
      hours_string
    );
  }

  const mins = duration.minutes();
  return _fmtreplace(
    _n('{0} ' + app_main_wpml.minute, '{0} ' + app_main_wpml.minutes, mins),
    mins
  );
};

export const formatOrderStatusLabel = (status) =>
  ORDER_STATUSES_LABELS[status]
    ? `${ORDER_STATUSES_LABELS[status]}`
    : app_main_wpml.unknown_status;

export const formatOrderStatusDescription = (status) =>
  ORDER_STATUSES_DESCRIPTIONS[status]
    ? `${ORDER_STATUSES_DESCRIPTIONS[status]}`
    : app_main_wpml.unknown_description;

export const formatOrderStatusClassName = (status, prefix = '') =>
  ORDER_STATUSES_CLASSNAMES[status]
    ? `${prefix}${ORDER_STATUSES_CLASSNAMES[status]}`
    : null;

//TODO: add final strings here
export const returnOrderRequestStatuses = (
  type,
  status,
  returnOutcome = false
) => {
  const requestStatuses = {
    voucher_to_giftcard: {
      initialStep: {
        title: TRANSLATIONS.SENDING_REQUEST,
        description: TRANSLATIONS.JUST_SUBMITTED_WITHDRAWAL_REQUEST,
      },
      pending: {
        title: TRANSLATIONS.IN_PROCESSING,
        description: TRANSLATIONS.REQUEST_IN_APPROVAL_PHASE_48_HOURS,
      },
      outcome: {
        pending: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.WILL_RECEIVE_EMAIL_WITH_THE_RESULT,
        },
        rejected: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.REFUSED_CANCELLATION,
        },
        approved: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.APPROVED_REQUEST,
        },
      },
      status: {
        pending: {
          title: TRANSLATIONS.PENDING_ACTIVITY_CHANGE,
          description: TRANSLATIONS.PENDING_ACTIVITY_CHANGE_DETAILS,
        },
        rejected: {
          title: TRANSLATIONS.VOUCHER_GIFT,
          description: TRANSLATIONS.CONFIRMED_VOUCHER_DESCRIPTION,
        },
      },
    },
    voucher_withdraw: {
      initialStep: {
        title: TRANSLATIONS.SENDING_REQUEST,
        description: TRANSLATIONS.JUST_SUBMITTED_WITHDRAWAL_REQUEST,
      },
      pending: {
        title: TRANSLATIONS.IN_PROCESSING,
        description: TRANSLATIONS.REQUEST_IN_APPROVAL_PHASE_72_HOURS,
      },
      outcome: {
        pending: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.WILL_RECEIVE_EMAIL_WITH_THE_RESULT,
        },
        rejected: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.REFUSED_CANCELLATION,
        },
        approved: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.APPROVED_REQUEST,
        },
      },
      status: {
        pending: {
          title: TRANSLATIONS.PENDING_CANCELLATION,
          description: TRANSLATIONS.PENDING_CANCELLATION_DETAILS,
        },
        rejected: {
          title: TRANSLATIONS.VOUCHER_GIFT,
          description: TRANSLATIONS.CONFIRMED_VOUCHER_DESCRIPTION,
        },
        approved: {
          title: TRANSLATIONS.CANCELLED_LABEL,
          description: '',
        },
      },
    },
    completed_booking_new_date: {
      initialStep: {
        title: TRANSLATIONS.RESCHEDULE_ACTIVITY_REQUEST,
        description: TRANSLATIONS.DEFINED_REQUEST_DETAILS,
      },
      pending: {
        title: TRANSLATIONS.IN_PROCESSING,
        description: TRANSLATIONS.REQUEST_IN_APPROVAL_PHASE_72_HOURS,
      },

      outcome: {
        pending: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.WILL_RECEIVE_EMAIL_WITH_THE_RESULT,
        },
        rejected: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.REQUEST_REJECTED,
        },
        approved: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.APPROVED_REQUEST,
        },
      },
      status: {
        pending: {
          title: TRANSLATIONS.RESCHEDULE_ACTIVITY,
          description: TRANSLATIONS.DATE_CHANGE_PROCEDURE_IN_PROGRESS_48H,
        },
        rejected: {
          title: TRANSLATIONS.ACTIVITY_COMPLETED,
          description: TRANSLATIONS.ACTIVITY_COMPLETED_NEXT_ADVENTURE,
        },
        approved: {
          title: TRANSLATIONS.CONFIRMED,
          description: TRANSLATIONS.REQUEST_APPROVED_BASED_ON_CHANGES,
        },
      },
    },
    completed_booking_refund: {
      initialStep: {
        title: TRANSLATIONS.REFUND_REQUEST,
        description: TRANSLATIONS.DEFINED_REFUND_REQUEST_DETAILS,
      },
      pending: {
        title: TRANSLATIONS.IN_PROCESSING,
        description: TRANSLATIONS.REQUEST_IN_APPROVAL_PHASE_72_HOURS,
      },

      outcome: {
        pending: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.WILL_RECEIVE_EMAIL_WITH_THE_RESULT,
        },
        rejected: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.REFUND_REJECTED,
        },
        approved: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.APPROVED_REQUEST,
        },
      },
      status: {
        pending: {
          title: TRANSLATIONS.REFUND_IN_PROGRESS,
          description: TRANSLATIONS.REFUND_PROCEDURE_IN_PROGRESS_72H,
        },
        rejected: {
          title: TRANSLATIONS.ACTIVITY_COMPLETED,
          description: TRANSLATIONS.ACTIVITY_COMPLETED_CHOOSE_NEXT_ADVENTURE,
        },
        approved: {
          title: TRANSLATIONS.REFUNDED,
          description: TRANSLATIONS.ORDER_REFUNDED_AS_REQUESTED,
        },
      },
    },
    completed_booking_to_voucher: {
      initialStep: {
        title: TRANSLATIONS.VOUCHER_REQUEST,
        description: TRANSLATIONS.DEFINED_VOUCHER_TRANSFORMATION_DETAILS,
      },
      pending: {
        title: TRANSLATIONS.IN_PROCESSING,
        description: TRANSLATIONS.REQUEST_IN_APPROVAL_PHASE_72_HOURS,
      },
      outcome: {
        pending: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.WILL_RECEIVE_EMAIL_WITH_THE_RESULT,
        },
        rejected: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.VOUCHER_TRANSFORMATION_REJECTED,
        },
        approved: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.APPROVED_REQUEST,
        },
      },
      status: {
        pending: {
          title: TRANSLATIONS.VOUCHER_TRANSFORMATION_IN_PROGRESS,
          description: TRANSLATIONS.TRANSFORMATION_PROCEDURE_IN_PROGRESS_72H,
        },
        rejected: {
          title: TRANSLATIONS.ACTIVITY_COMPLETED,
          description: TRANSLATIONS.ACTIVITY_COMPLETED_CHOOSE_NEXT_ADVENTURE,
        },
      },
    },
    confirmed_booking_new_date: {
      initialStep: {
        title: TRANSLATIONS.RESCHEDULE_ACTIVITY_REQUEST,
        description: TRANSLATIONS.DEFINED_REQUEST_DETAILS,
      },
      pending: {
        title: TRANSLATIONS.IN_PROCESSING,
        description: TRANSLATIONS.REQUEST_IN_APPROVAL_PHASE_72_HOURS,
      },

      outcome: {
        pending: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.WILL_RECEIVE_EMAIL_WITH_THE_RESULT,
        },
        rejected: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.REQUEST_REJECTED,
        },
        approved: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.APPROVED_REQUEST,
        },
      },
      status: {
        pending: {
          title: TRANSLATIONS.RESCHEDULE_ACTIVITY,
          description: TRANSLATIONS.DATE_CHANGE_PROCEDURE_IN_PROGRESS_48H,
        },
        rejected: {
          title: TRANSLATIONS.CONFIRMED,
          description: TRANSLATIONS.BOOKING_CONFIRMED_ENJOY,
        },
        approved: {
          title: TRANSLATIONS.CONFIRMED,
          description: TRANSLATIONS.BOOKING_CONFIRMED_ENJOY,
        },
      },
    },
    confirmed_booking_refund: {
      initialStep: {
        title: TRANSLATIONS.REFUND_REQUEST,
        description: TRANSLATIONS.DEFINED_REFUND_REQUEST_DETAILS,
      },
      pending: {
        title: TRANSLATIONS.IN_PROCESSING,
        description: TRANSLATIONS.REQUEST_IN_APPROVAL_PHASE_72_HOURS,
      },

      outcome: {
        pending: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.WILL_RECEIVE_EMAIL_WITH_THE_RESULT,
        },
        rejected: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.REFUND_REJECTED,
        },
        approved: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.APPROVED_REQUEST,
        },
      },
      status: {
        pending: {
          title: TRANSLATIONS.REFUND_IN_PROGRESS,
          description: TRANSLATIONS.REFUND_PROCEDURE_IN_PROGRESS_72H,
        },
        rejected: {
          title: TRANSLATIONS.ACTIVITY_COMPLETED,
          description: TRANSLATIONS.ACTIVITY_COMPLETED_CHOOSE_NEXT_ADVENTURE,
        },
        approved: {
          title: TRANSLATIONS.REFUNDED,
          description: TRANSLATIONS.ORDER_REFUNDED_AS_REQUESTED,
        },
      },
    },
    confirmed_booking_to_voucher: {
      initialStep: {
        title: TRANSLATIONS.VOUCHER_REQUEST,
        description: TRANSLATIONS.DEFINED_VOUCHER_TRANSFORMATION_DETAILS,
      },
      pending: {
        title: TRANSLATIONS.IN_PROCESSING,
        description: TRANSLATIONS.REQUEST_IN_APPROVAL_PHASE_72_HOURS,
      },
      outcome: {
        pending: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.WILL_RECEIVE_EMAIL_WITH_THE_RESULT,
        },
        rejected: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.VOUCHER_TRANSFORMATION_REJECTED,
        },
        approved: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.APPROVED_REQUEST,
        },
      },
      status: {
        pending: {
          title: TRANSLATIONS.VOUCHER_TRANSFORMATION_IN_PROGRESS,
          description: TRANSLATIONS.TRANSFORMATION_PROCEDURE_IN_PROGRESS_72H,
        },
        rejected: {
          title: TRANSLATIONS.ACTIVITY_COMPLETED,
          description: TRANSLATIONS.ACTIVITY_COMPLETED_CHOOSE_NEXT_ADVENTURE,
        },
      },
    },
    confirmed_booking_to_giftcard: {
      initialStep: {
        title: TRANSLATIONS.GIFTCARD_REQUEST,
        description: TRANSLATIONS.DEFINED_GIFTCARD_TRANSFORMATION_DETAILS,
      },
      pending: {
        title: TRANSLATIONS.IN_PROCESSING,
        description: TRANSLATIONS.REQUEST_IN_APPROVAL_PHASE_72_HOURS,
      },
      outcome: {
        pending: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.WILL_RECEIVE_EMAIL_WITH_THE_RESULT,
        },
        rejected: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.GIFTCARD_TRANSFORMATION_REJECTED,
        },
        approved: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.APPROVED_REQUEST,
        },
      },
      status: {
        pending: {
          title: TRANSLATIONS.GIFTCARD_TRANSFORMATION_IN_PROGRESS,
          description: TRANSLATIONS.TRANSFORMATION_PROCEDURE_IN_PROGRESS_72H,
        },
        rejected: {
          title: TRANSLATIONS.ACTIVITY_COMPLETED,
          description: TRANSLATIONS.ACTIVITY_COMPLETED_CHOOSE_NEXT_ADVENTURE,
        },
      },
    },
    onhold_new_date: {
      initialStep: {
        title: TRANSLATIONS.RESCHEDULE_ACTIVITY_REQUEST,
        description: TRANSLATIONS.DEFINED_REQUEST_DETAILS,
      },
      pending: {
        title: TRANSLATIONS.IN_PROCESSING,
        description: TRANSLATIONS.REQUEST_IN_APPROVAL_PHASE_72_HOURS,
      },

      outcome: {
        pending: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.WILL_RECEIVE_EMAIL_WITH_THE_RESULT,
        },
        rejected: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.REQUEST_REJECTED,
        },
        approved: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.APPROVED_REQUEST,
        },
      },
      status: {
        pending: {
          title: TRANSLATIONS.RESCHEDULE_ACTIVITY,
          description: TRANSLATIONS.DATE_CHANGE_PROCEDURE_IN_PROGRESS_48H,
        },
        rejected: {
          title: TRANSLATIONS.WAITING_FOR_CONFIRMATION,
          description: TRANSLATIONS.BOOKING_REQUEST_WAITING_GUIDE_CONFIRMATION,
        },
        approved: {
          title: TRANSLATIONS.CONFIRMED,
          description: TRANSLATIONS.BOOKING_CONFIRMED_ENJOY,
        },
      },
    },
    onhold_refund: {
      initialStep: {
        title: TRANSLATIONS.REFUND_REQUEST,
        description: TRANSLATIONS.DEFINED_REFUND_REQUEST_DETAILS,
      },
      pending: {
        title: TRANSLATIONS.IN_PROCESSING,
        description: TRANSLATIONS.REQUEST_IN_APPROVAL_PHASE_72_HOURS,
      },

      outcome: {
        pending: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.WILL_RECEIVE_EMAIL_WITH_THE_RESULT,
        },
        rejected: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.REFUND_REJECTED,
        },
        approved: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.APPROVED_REQUEST,
        },
      },
      status: {
        pending: {
          title: TRANSLATIONS.REFUND_IN_PROGRESS,
          description: TRANSLATIONS.REFUND_PROCEDURE_IN_PROGRESS_72H,
        },
        rejected: {
          title: TRANSLATIONS.ACTIVITY_COMPLETED,
          description: TRANSLATIONS.ACTIVITY_COMPLETED_CHOOSE_NEXT_ADVENTURE,
        },
        approved: {
          title: TRANSLATIONS.REFUNDED,
          description: TRANSLATIONS.ORDER_REFUNDED_AS_REQUESTED,
        },
      },
    },
    onhold_to_voucher: {
      initialStep: {
        title: TRANSLATIONS.VOUCHER_REQUEST,
        description: TRANSLATIONS.DEFINED_VOUCHER_TRANSFORMATION_DETAILS,
      },
      pending: {
        title: TRANSLATIONS.IN_PROCESSING,
        description: TRANSLATIONS.REQUEST_IN_APPROVAL_PHASE_72_HOURS,
      },
      outcome: {
        pending: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.WILL_RECEIVE_EMAIL_WITH_THE_RESULT,
        },
        rejected: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.VOUCHER_TRANSFORMATION_REJECTED,
        },
        approved: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.APPROVED_REQUEST,
        },
      },
      status: {
        pending: {
          title: TRANSLATIONS.VOUCHER_TRANSFORMATION_IN_PROGRESS,
          description: TRANSLATIONS.TRANSFORMATION_PROCEDURE_IN_PROGRESS_72H,
        },
        rejected: {
          title: TRANSLATIONS.ACTIVITY_COMPLETED,
          description: TRANSLATIONS.ACTIVITY_COMPLETED_CHOOSE_NEXT_ADVENTURE,
        },
      },
    },
    onhold_to_giftcard: {
      initialStep: {
        title: TRANSLATIONS.GIFTCARD_REQUEST,
        description: TRANSLATIONS.DEFINED_GIFTCARD_TRANSFORMATION_DETAILS,
      },
      pending: {
        title: TRANSLATIONS.IN_PROCESSING,
        description: TRANSLATIONS.REQUEST_IN_APPROVAL_PHASE_72_HOURS,
      },
      outcome: {
        pending: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.WILL_RECEIVE_EMAIL_WITH_THE_RESULT,
        },
        rejected: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.GIFTCARD_TRANSFORMATION_REJECTED,
        },
        approved: {
          title: TRANSLATIONS.OUTCOME,
          description: TRANSLATIONS.APPROVED_REQUEST,
        },
      },
      status: {
        pending: {
          title: TRANSLATIONS.GIFTCARD_TRANSFORMATION_IN_PROGRESS,
          description: TRANSLATIONS.TRANSFORMATION_PROCEDURE_IN_PROGRESS_72H,
        },
        rejected: {
          title: TRANSLATIONS.ACTIVITY_COMPLETED,
          description: TRANSLATIONS.ACTIVITY_COMPLETED_CHOOSE_NEXT_ADVENTURE,
        },
      },
    },
    giftcard_delete: {
      initialStep: {
        title: TRANSLATIONS.SENDING_REQUEST,
        description: TRANSLATIONS.SUBMITTED_CANCELLATION_REQUEST,
      },
      pending: {
        title: TRANSLATIONS.ACTIVITY_CANCELLATION_IN_PROGRESS,
        description: TRANSLATIONS.ACTIVITY_CANCELLATION_PROCEDURE_IN_PROGRESS,
      },
      outcome: {
        pending: {
          title: TRANSLATIONS.REQUEST_PENDING,
          description: TRANSLATIONS.FINAL_DESCRIPTION_PLACEHOLDER,
        },
        rejected: {
          title: TRANSLATIONS.REQUEST_REJECTED_STATUS,
          description: TRANSLATIONS.FINAL_DESCRIPTION_PLACEHOLDER,
        },
        approved: {
          title: TRANSLATIONS.REQUEST_APPROVED_STATUS,
          description: TRANSLATIONS.FINAL_DESCRIPTION_PLACEHOLDER,
        },
      },
      status: {
        pending: {
          title: TRANSLATIONS.ACTIVITY_CANCELLATION_IN_PROGRESS,
          description: TRANSLATIONS.ACTIVITY_CANCELLATION_PROCEDURE_IN_PROGRESS,
        },
        rejected: {
          title: TRANSLATIONS.REQUEST_REJECTED_STATUS,
          description: TRANSLATIONS.FINAL_DESCRIPTION_PLACEHOLDER,
        },
        approved: {
          title: TRANSLATIONS.REQUEST_APPROVED_STATUS,
          description: TRANSLATIONS.FINAL_DESCRIPTION_PLACEHOLDER,
        },
      },
    },
  };

  const values = { ...requestStatuses[type] };

  if (!values) return null;

  if (returnOutcome) {
    const outcome = values.outcome;

    if (status === 'pending') {
      return { ...values, outcome: outcome.pending };
    } else if (outcome[status]) {
      return { ...values, outcome: outcome[status] };
    }
  } else {
    return values['status'][status] || values[status];
  }

  return values;
};
